/* eslint-disable */

import {
  Button,
  Col,
  Input,
  Popconfirm,
  Row,
  Select,
  DatePicker,
  Tooltip,
  Checkbox,
  Divider,
  Switch,
} from "antd";
import {
  InfoCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import FormLayout from "../../FormLayout/FormLayout";
import DateField from "../../Fields/DateField/DateField";
import CountryField from "../../Fields/CountryField/CountryField";
import NameField from "../../Fields/NameField/NameField";
import LastNameField from "../../Fields/LastNameField/LastNameField";
import DocumentTypeField from "../../Fields/DocumentTypeField/DocumentTypeField";
import SecondLastNameField from "../../Fields/SecondLastNameField/SecondLastNameField";
import EmailField from '../../Fields/EmailField/EmailField';
import TaxIdentificationField from '../../Fields/TaxIdentificationField/TaxIdentificationField';

import useRepresentativesForm from "../../../Hooks/useRepresentativesForm/useRepresentativesForm";
import { InvestorAPI } from "../../../Api/InvestorAPI";
import { ContractAPI } from "../../../Api/ContractAPI";
import { RepresentativesAPI } from "../../../Api/RepresentativesAPI";

import { IRepresentativesForm } from "./IRepresentativesForm";
import { Representative } from "../../../Models";

import TextField from "../../Fields/TextField/TextField";

import "./RepresentativesForm.scss";

const { Option } = Select;

const RepresentativesForm = ({
	baseURL,
	baseURLExternal,
	translations,
	unlockNextStep,
	clientId,
	language,
	personType,
	isReadOnly = false
}: IRepresentativesForm) => {
	const representativesAPI = RepresentativesAPI(baseURL, clientId);
	const investorAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const {
		placesService,
		placePredictions,
		getPlacePredictions,
		isPlacePredictionsLoading,
	} = usePlacesService({ apiKey: "AIzaSyCU9qZlMZtXtxKvRJi-NRNJtPJF0p7DpYo" });

	const {
		loading,
		noRepresentativesError,
		errors,
		nationalitiesList,
		representatives,
		contractId,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		addRow,
		deleteRow,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		handleChangeSwitch,
		handleCheckedData,
		handleChangePRPData,
		saveData,
		handleSelectAddress,
		handleSearchAddress,
	} = useRepresentativesForm({
		translations,
		representativesAPI,
		investorAPI,
		contractAPI,
		placesService,
		unlockNextStep,
		getPlacePredictions,
		personType
	});
	
	const enableNextStep = personType === 2 ? (representatives.length > 0 && representatives[0]?.id > 0) : true;

  	return (
		<FormLayout
			translations={translations}
			loading={loading}
			title={translations.representativeAttorneyData}
			onClick={saveData}
			contractId={contractId}
			nextStep={unlockNextStep}
			enableNextStep={enableNextStep}
			disabledSaveButton={!representatives?.length}
			forceEnableNextStep={isReadOnly}
			hideSaveButton={isReadOnly}
		>
     		<Row justify="space-between">
				<Col>
					<Button
						type="default"
						className="save-btn"
						/* disabled={maxNumSignatories} */
						onClick={addRow}
						disabled={isReadOnly}
					>
						<PlusCircleOutlined />
						{translations.representativeAttorney}
					</Button>
				</Col>
     		</Row>
			{noRepresentativesError ? (
				<span style={{ color: "red" }}>
					Debe agregar al menos un representante
				</span>
			) : null}
      		{
				representatives.map((representative: Representative, index: number) => (
					<Col xs={24} md={24} lg={24} className="signatorieRow">
						<Row gutter={[16, 16]} align="top">
							<NameField
								name={ representative.name }
								translations={ translations}
								errors={ errors }
								index={ index }
								handleChangeData={ handleChangeData }
								disabled={ isReadOnly }
							/>
							<LastNameField
								lastName={ representative.lastName }
								translations={ translations }
								errors={ errors }
								index={ index }
								handleChangeData={ handleChangeData }
								disabled={ isReadOnly }
							/>
							<SecondLastNameField
								lastName={ representative.secondLastName! }
								translations={ translations }
								errors={ errors }
								index={ index }
								handleChangeData={ handleChangeData }
								required={representative.nationality === 'Portugal'}
								disabled={ isReadOnly }
							/>
							<Col xs={24} md={6} lg={6} className="titles">
								<label className="required">
									{translations.representativeAttorney} 
									<Tooltip placement="topLeft" title={translations.representationTooltip}>
										<InfoCircleOutlined className="tooltip" />
									</Tooltip>
								</label>
								<Select
									value={representative.administrator}
									placeholder={translations.representativeAttorney}
									className="input-field"
									onChange={handleSelectData("administrator", index)}
									disabled={isReadOnly}
									status={errors[index].administrator ? "error" : ""}
								>
									{translations.administratorOptions.map((option: string, idx: number) => <Option key={idx} value={option}>{option}</Option>)}
								</Select>
								<span className={`error ${errors[index].administrator ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<Col xs={24} md={6} lg={6} className="titles">
								<label className="required">
									{translations.representationType}
								</label>
								<Select
									value={representative.administratorType}
									className="input-field"
									placeholder={translations.representationType}
									onChange={handleSelectData("administratorType", index)}
									disabled={isReadOnly}
									status={errors[index].administratorType ? "error" : ""}
								>
								{translations.administratorTypeOptions.map((option: string, idx: number) => <Option key={idx} value={option}>{option}</Option>)}
								</Select>
								<span className={`error ${ errors[index].administratorType ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<Col xs={24} md={6} lg={6} className="titles">
								<label className="required">
									{translations.birthDate}
								</label>
								<DatePicker
									allowClear={false}
									className="input-field"
									name="birthDate"
									value={representative.birthDate ? moment(representative.birthDate) : undefined}
									format="DD/MM/YYYY"
									onChange={handleChangeDate("birthDate", index)}
									disabled={isReadOnly}
									status={errors[index].birthDate ? "error" : ""}
								/>
								<span className={`error ${errors[index].birthDate ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<DocumentTypeField
								value={representative.documentType}
								translations={translations}
								errors={errors}
								index={index}
								handleSelect={handleSelectData}
								disabled={isReadOnly}
							/>
							<Col xs={24} md={6} lg={6} className="titles">
								<label className="required">{translations.docNumber}</label>
								<Input
									type="text"
									name="documentNumber"
									value={representative.documentNumber}
									disabled={isReadOnly}
									onChange={handleChangeData(index)}
									status={errors[index].documentNumber ? "error" : ""}
									placeholder={translations.documentNumber}
								/>
								<span className={`error ${errors[index].documentNumber ? "show" : null}`}>
									{translations.completeCampo}
								</span>
								<span className={`error ${errors[index].notValidDocument ? 'show' : null}`}>{translations.invalidIdDocument}</span>
							</Col>
							<Col xs={24} md={6} lg={6} className="titles">
								<label className="required">
									{translations.expirationDate}
								</label>
								<DatePicker
									allowClear={false}
									className="input-field"
									value={
										representative.documentExpiration ? moment(representative.documentExpiration): undefined
									}
									format="DD/MM/YYYY"
									onChange={handleChangeDate("documentExpiration", index)}
									disabled={isReadOnly}
									status={errors[index].documentExpiration ? "error" : ""}
								/>
								<span className={`error ${errors[index].documentExpiration ? "show" : null}`}>
									{translations.completeCampo}
								</span>
								<span className={`error ${errors[index].notValidExpirationDate ? 'show' : null}`}>{translations.invalidDate}</span>
							</Col>
							<EmailField
								value={ representative.email }
								fieldName='email'
								translations={ translations }
								errors={ errors }
								index={ index }
								handleChangeData={ handleChangeData }
								disabled={ isReadOnly }
							/>
						 	<TextField
								size='s'
								value={representative.phonePrefix}
								fieldName="phonePrefix"
								placeholder="+34"
								translations={translations}
								required={false}
								errors={errors}
								index={index}
								handleChange={handleChangeData}
								disabled={isReadOnly}
							/>
							<TextField
								size='s'
								value={representative.phoneNumber}
								fieldName="phoneNumber"
								placeholder="000 000 000"
								translations={translations}
								required={false}
								errors={errors}
								index={index}
								handleChange={handleChangeData}
								disabled={isReadOnly}
							/>
							<CountryField
								value={representative.nationality}
								fieldName="nationality"
								nationalitiesList={nationalitiesList}
								translations={translations}
								errors={errors}
								index={index}
								handleSelect={handleSelectData}
								language={language}
								contractLanguage={language}
								disabled={isReadOnly}
							/>
							<TaxIdentificationField
								placeholder={translations.taxIdentification}
								value={representative.taxIdentification}
								fieldName="taxIdentification"
								translations={translations}
								errors={errors}
								index={index}
								handleChange={handleChangeData}
								disabled={isReadOnly}
							/>
							<Col xs={24} md={4} lg={4} className="titles">
								<div className="isSignatory">
									<label> {translations.isSignatory}</label>
									<Switch
										disabled
										checked={representative.isSignatory}
										onChange={handleChangeSwitch("isSignatory", index)}
									/>
								</div>
							</Col>
						</Row>
						<Row gutter={[16, 8]} justify="start" className="main-residence-title">
							<Col xs={24} md={12} lg={12}>
								<label className="required"> {translations.taxResidence} </label>
								<Select
									style={{ width: "100%" }}
									showSearch
									value={taxAddressInputs[index]}
									placeholder={translations.direccion}
									disabled={isReadOnly}
									onSearch={handleSearchAddress(index, 'taxResidence')}
									onChange={handleSelectAddress(index, 'taxResidence')}
									filterOption={false} // Importante: para que solo muestre las opciones de Google
									notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
								>
									{activeAddress && activeAddress.index === index && activeAddress.field === "taxResidence"
										? placePredictions.map((item) => (
											<Option key={item.place_id} value={item.description}>
											{item.description}
											</Option>
										))
										: null
									}
								</Select>
								<span className={`error ${errors[index].taxResidence ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<TextField
								size='m'
								value={representative.taxResidenceExtra}
								fieldName="taxResidenceExtra"
								placeholder={translations.taxResidenceExtra}
								translations={translations}
								required={false}
								index={index}
								errors={errors}
								handleChange={handleChangeData}
								disabled={isReadOnly}
							/>
						</Row>
						{manualAddressVisibility[index]?.taxResidence && (
							<Row gutter={[16, 8]} justify="start">
								<TextField
									size='m'
									label={translations.direccion}
									value={representative.taxResidence}
									fieldName="taxResidence"
									errorWhenEmpty
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<TextField
									size='s'
									value={representative.taxResidencePostalCode}
									fieldName="taxResidencePostalCode"
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<TextField
									size='s'
									value={representative.taxResidenceCity}
									fieldName="taxResidenceCity"
									translations={translations}
									required
									errors={errors}
									index={index}
									handleChange={handleChangeData}
									disabled={isReadOnly}
								/>
								<CountryField
									value={representative.countryTaxResidence}
									fieldName="countryTaxResidence"
									nationalitiesList={nationalitiesList}
									translations={translations}
									errors={errors}
									index={index}
									handleSelect={handleSelectData}
									language={language}
									contractLanguage={language}
									disabled={isReadOnly}
								/>
							</Row>
						)}
						<Row gutter={[16, 16]}>
							{
								!representative.isSignatory ? 
									<Col xs={24} md={2} lg={2} className="titles" >
										<Popconfirm
										title={translations.eliminarFirmante}
										okText={translations.si}
										cancelText={translations.no}
										onConfirm={() => deleteRow(index)}
										disabled={isReadOnly}
										>
										<Button danger className="dlt-btn" disabled={isReadOnly}>{translations.delete}</Button>
										</Popconfirm>
									</Col>
								: null
							}
						</Row>
						<Divider />
					</Col>
				))
      		}
			{ representatives.length > 0 ? (
				<Row gutter={[16, 16]} align="middle" className="prp5Q">
					<Col xs={24} md={24} lg={24}>
						<Checkbox
							className='qualitas-checkbox'
							name="prP5"
							checked={representatives[0]?.prP5}
							disabled={isReadOnly}
							onChange={handleCheckedData(0)}
						>
							{translations.isRepresentativePRP5} 
							<Tooltip placement="topLeft" title={translations.prpTooltip}>
								<InfoCircleOutlined className="tooltip" />
							</Tooltip>
						</Checkbox>
					</Col>
				</Row>
			) : 
				<Col xs={24} md={24} lg={24} className="signatorieRow">
						<p className="addRepresentative-message">{translations.addRepresentatives} </p>
				</Col>
			}
      		{
				representatives[0]?.prP5 ?
					(
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required">{translations.completeNamePRP51}</label>
								<Input
									type="text"
									name="namePRP51"
									value={representatives[0]?.namePRP51}
									disabled={isReadOnly}
									onChange={handleChangePRPData}
									status={errors[0].namePRP51 ? "error" : ""}
								/>
								<span className={`error ${errors[0].namePRP51 ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required">{translations.positionPRP51}</label>
								<Input
									type="text"
									name="positionPRP51"
									value={representatives[0]?.positionPRP51}
									disabled={isReadOnly}
									onChange={handleChangePRPData}
									status={errors[0].positionPRP51 ? "error" : ""}
								/>
								<span className={`error ${errors[0].positionPRP51 ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label> {translations.completeNamePRP52}</label>
								<Input
									type="text"
									name="namePRP52"
									value={representatives[0]?.namePRP52}
									disabled={isReadOnly}
									onChange={handleChangePRPData}
									status={errors[0].namePRP52 ? "error" : ""}
								/>
								<span className={`error ${errors[0].namePRP52 ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label> {translations.positionPRP52}</label>
								<Input
									type="text"
									name="positionPRP52"
									value={representatives[0]?.positionPRP52}
									disabled={isReadOnly}
									onChange={handleChangePRPData}
									status={errors[0].positionPRP52 ? "error" : ""}
								/>
								<span className={`error ${errors[0].positionPRP52 ? "show" : null}`}>
									{translations.completeCampo}
								</span>
							</Col>
						</Row>
					)
					: null
			}
    	</FormLayout>
  	);
};

export default RepresentativesForm;
